import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const AuthContext = createContext();

const AUTH_API_URL = "https://apiweb.hitpoly.com/ajax/auth.php";

// Helper para decodificar JWT (simplificado para frontend)
const jwtDecode = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(atob(base64).split("").map(function(c) {
      return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(""));
    const decoded = JSON.parse(jsonPayload);
    return decoded.data; // El payload real está dentro de la clave \"data\"
  } catch (e) {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Función para establecer la sesión (cuando se recibe un nuevo token)
  const login = useCallback((jwtToken) => {
    localStorage.setItem("jwt_token", jwtToken);
    setToken(jwtToken);
    const decodedUser = jwtDecode(jwtToken);
    if (decodedUser) {
      setUser(decodedUser);
      setIsAuthenticated(true);
    } else {
      // Si el token es inválido al decodificar, forzar logout
      logout(); 
    }
  }, []);

  // Función de logout
  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem("jwt_token");
    // Opcional: limpiar todos los elementos del localStorage si no hay otros datos que necesites
    // localStorage.clear(); 
    // sessionStorage.clear();

    window.location.href = "/"; // Redirigir al inicio
  }, []);

  // Carga inicial y validación del token al montar la app
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get("token") || urlParams.get("jwt");

    if (urlToken) {
      const decodedUser = jwtDecode(urlToken);
      if (decodedUser) {
        localStorage.setItem("jwt_token", urlToken);
        setUser(decodedUser);
        setToken(urlToken);
        setIsAuthenticated(true);
      }
      setLoading(false);
      return;
    }

    const storedToken = localStorage.getItem("jwt_token");

    if (storedToken) {
      const decodedUser = jwtDecode(storedToken);
      if (decodedUser) {
        setUser(decodedUser);
        setToken(storedToken);
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem("jwt_token");
      }
    }
    setLoading(false);
  }, [logout]);

  // Login con Correo y Contraseña
  const loginWithEmail = async (email, password) => {
    try {
      const response = await fetch(AUTH_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "login", email, password }),
      });

      const data = await response.json();

      if (response.ok && data.status === "success" && data.token) {
        login(data.token);
        return data; // Devuelve el user y token del backend
      } else {
        throw new Error(data.message || "Error al iniciar sesión");
      }
    } catch (error) {
      throw error;
    }
  };

  // Verificar disponibilidad de username
  const checkUsername = async (username) => {
    try {
      const response = await fetch(AUTH_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "check_username", username }),
      });
      return await response.json();
    } catch (error) {
      throw error;
    }
  };

  // Obtener sugerencias iniciales
  const getSuggestions = async (firstName, lastName) => {
    try {
      const response = await fetch(AUTH_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "get_suggestions", firstName, lastName }),
      });
      return await response.json();
    } catch (error) {
      return { status: "error", sugerencias: [] };
    }
  };

  // Enviar código de verificación al correo de recuperación
  const sendVerificationCode = async (recovery_email) => {
    try {
      const response = await fetch(AUTH_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ action: "send_verification", recovery_email }),
      });
      return await response.json();
    } catch (error) {
      throw error;
    }
  };

  // Registro con Hitpoly Username y Código
  const registerWithEmail = async (name, hitpoly_username, password, recovery_email, code, verification_hash, apellido = '') => {
    try {
      const response = await fetch(AUTH_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ action: "register", name, apellido, hitpoly_username, password, recovery_email, code, verification_hash }),
      });

      const data = await response.json();

      if (response.ok && data.status === "success" && data.token) {
        login(data.token); // Inicia sesión automáticamente después del registro exitoso
        return data;
      } else {
        throw new Error(data.message || "Error al registrar");
      }
    } catch (error) {
      throw error;
    }
  };

  // Provee el token en las peticiones (ej. a otras APIs)
  const authFetch = useCallback(async (url, options = {}) => {
    const headers = { ...options.headers };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    options.headers = headers;
    return fetch(url, options);
  }, [token]);

  // Solicitar recuperación de contraseña
  const requestPasswordReset = async (identifier) => {
    try {
      const response = await fetch(AUTH_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "request_password_reset", identifier }),
      });
      return await response.json();
    } catch (error) {
      return { status: "error", message: "Error al solicitar recuperación" };
    }
  };

  // Actualizar contraseña con token
  const updatePasswordWithToken = async (token, new_password) => {
    try {
      const response = await fetch(AUTH_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_password_with_token", token, new_password }),
      });
      return await response.json();
    } catch (error) {
      return { status: "error", message: "Error al actualizar contraseña" };
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user,
      token,
      isAuthenticated,
      loading,
      login,
      logout,
      loginWithEmail,
      registerWithEmail,
      checkUsername,
      getSuggestions,
      sendVerificationCode,
      requestPasswordReset,
      updatePasswordWithToken,
      authFetch // Exporta la función de fetch con auth
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
